import { useState, useRef, type ChangeEvent } from 'react';
import { uploadFile } from '../services/uploadService';
import type { AxiosProgressEvent } from 'axios';
import { getDecryptedToken } from '../../../services/tokenStorage';

export const useUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const [progress, setProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setUploadError(null);
    setStatusMessage(null);
    setUploadResponse(null);
    setProgress(0);
  };

  const handleUpload = async (uploadType: 'sales_order' | 'sellin' | 'ats') => {
    if (!file) {
      setUploadError('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setStatusMessage(null);
    setProgress(0);

    try {
      const token = getDecryptedToken('idToken');
      const res = await uploadFile(file, uploadType, token, (progressEvent: AxiosProgressEvent) => {
        const loaded = progressEvent.loaded ?? 0;
        const total = progressEvent.total ?? 1;
        const percent = Math.round((loaded * 100) / total);
        setProgress(percent);
      });

      if (res.status === 200 && res.data?.success) {
        setUploadResponse(res.data);
        setStatusMessage('Upload successful');
        setFile(null);
        setProgress(100);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else if (res.status === 400) {
        setStatusMessage('⚠️ Bad request — please check your file');
      } else {
        setStatusMessage(`ℹ️ Status: ${res.status}`);
      }
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 400) {
        setUploadError(err.response.data?.detail || 'Bad request');
      } else if (status === 413) {
        setUploadError('File size too large');
      } else {
        setUploadError(`Error ${status || ''}: ${err.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  return {
    file,
    uploading,
    progress, 
    uploadError,
    uploadResponse,
    statusMessage,
    fileInputRef,
    handleFileChange,
    handleUpload,
  };
};
